import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileText, Upload, Loader2, CheckCircle } from 'lucide-react';
import { useUploadFiscalDocuments } from '@/hooks/use-fiscal-documents';

interface FiscalDocumentsUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  companyId: string;
  invoiceNumber: string;
  hasDocs?: boolean;
}

export function FiscalDocumentsUploadModal({
  isOpen,
  onClose,
  invoiceId,
  companyId,
  invoiceNumber,
  hasDocs = false
}: FiscalDocumentsUploadModalProps) {
  const [danfePdf, setDanfePdf] = useState<File | null>(null);
  const [xmlFile, setXmlFile] = useState<File | null>(null);
  
  const uploadMutation = useUploadFiscalDocuments();

  const handleSubmit = async () => {
    if (!danfePdf || !xmlFile) {
      return;
    }

    await uploadMutation.mutateAsync({
      invoiceId,
      companyId,
      danfePdf,
      xmlFile
    });

    onClose();
  };

  const handleClose = () => {
    setDanfePdf(null);
    setXmlFile(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {hasDocs ? 'Atualizar' : 'Anexar'} Documentos Fiscais
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <FileText className="h-4 w-4" />
            <AlertDescription>
              Fatura: <strong>{invoiceNumber}</strong>
              {hasDocs && (
                <p className="text-xs text-muted-foreground mt-1">
                  Os documentos atuais serão substituídos.
                </p>
              )}
            </AlertDescription>
          </Alert>

          {/* Upload DANFE */}
          <div className="space-y-2">
            <Label htmlFor="danfe">
              DANFE (PDF) <span className="text-destructive">*</span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="danfe"
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => setDanfePdf(e.target.files?.[0] || null)}
                disabled={uploadMutation.isPending}
              />
              {danfePdf && (
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Arquivo PDF da DANFE (máx. 10MB)
            </p>
          </div>

          {/* Upload XML */}
          <div className="space-y-2">
            <Label htmlFor="xml">
              XML <span className="text-destructive">*</span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="xml"
                type="file"
                accept=".xml,application/xml,text/xml"
                onChange={(e) => setXmlFile(e.target.files?.[0] || null)}
                disabled={uploadMutation.isPending}
              />
              {xmlFile && (
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Arquivo XML da nota fiscal (máx. 10MB)
            </p>
          </div>

          <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
            <Upload className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-blue-800 dark:text-blue-200">
              Os documentos fiscais ficarão disponíveis para download na área de faturas.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={uploadMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!danfePdf || !xmlFile || uploadMutation.isPending}
          >
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                {hasDocs ? 'Atualizar' : 'Anexar'} Documentos
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
